/* eslint-disable react-hooks/exhaustive-deps */
import ContentShell from "components/ContentShell";
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Box, Button, Divider, Typography } from "@mui/material";
import configData from "config/config.json";
import { useSnackbar } from "hooks/useSnackbar";
import { SimpleSelectInput } from "components/FormSelectInput";
import sessionHelper from "services/sessionHelper";
import { makeStyles } from "@mui/styles";
import {
  createQuickBet,
  getQuickBetInfo,
  updateQuickBet,
} from "services/quickBetService";
import * as Yup from "yup";
import { SimpleTextInput } from "components/FormTextInput";

const useStyles = makeStyles((theme) => ({
  field: {
    width: 220,
  },
  chipContainer: {
    display: "flex",
    flexFlow: "row",
    gap: 2,
    overflow: "scroll",
    width: "calc(100% - 300px)",
    margin: "26px auto auto 0px",
  },
  bodyContainer: {
    display: "flex",
    marginTop: 10,
    gap: 8,
    alignItems: "flex-end",
  },
  firstFormContainer: {
    display: "flex",
    flexFlow: "row wrap",
    gap: 4,
    margin: "0px 0px 10px 0px",
  },
  betContainer: {
    position: "relative",
    display: "flex",
    flexDirection: "row",
    minWidth: 140,
    gap: 10,
    width: "calc(100% - 140px)",
  },
  actionBtnContainer: {
    display: "flex",
    gap: 10,
  },
}));

const quickBetSchema = Yup.object().shape({
  bets: Yup.array()
    .of(Yup.number().positive().min(1).typeError("Each bet must be a number"))
    .length(4, "Exactly 4 bets are required"),
  currency: Yup.string().required("Currency is required"),
  territory: Yup.string().required("Territory is required"),
});

interface QuickBetDetails {
  bets: any[];
  currency: string;
  territory: string;
  _id?: string;
}

const _quickBetDetails: QuickBetDetails = {
  bets: [],
  currency: "",
  territory: "",
};

const QuickBetEditor = () => {
  const styles = useStyles();
  const location = useLocation();
  const { showSnackbar } = useSnackbar();
  const id = location.pathname.split("/").slice(-1)[0];
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [quickBetDetails, setQuickBetDetails] = useState(_quickBetDetails);

  const cancel = () => navigate(configData.routes.quickBet.quickBet);

  const submit = async () => {
    try {
      setLoading(true);
      let response;
      await quickBetSchema.validate(quickBetDetails);
      if (id === "create") {
        response = await createQuickBet({
          ...quickBetDetails,
          bets: quickBetDetails.bets.map((bet) => parseInt(bet)),
        });
      } else {
        response = await updateQuickBet({
          _id: quickBetDetails?._id,
          bets: quickBetDetails.bets.map((bet) => parseInt(bet)),
          currency: quickBetDetails.currency,
          territory: quickBetDetails.territory,
        });
      }
      showSnackbar(response.message);
      setLoading(false);
      navigate(configData.routes.quickBet.quickBet);
    } catch (error) {
      setLoading(false);
      showSnackbar(error.message, "error");
    }
  };

  const onChange = async (e, index) => {
    const { name, value } = e?.target;

    setQuickBetDetails((state) => {
      if (name === "bets") {
        return {
          ...state,
          bets: [
            ...state.bets.slice(0, index),
            value,
            ...state.bets.slice(index + 1),
          ],
        };
      }
      return { ...state, [name]: value };
    });
  };

  const fetchInfo = async () => {
    try {
      setLoading(true);
      const resp = await getQuickBetInfo(id);
      setQuickBetDetails(resp.data);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      showSnackbar(error.message);
    }
  };

  useEffect(() => {
    id !== "create" && fetchInfo();
  }, []);

  return (
    <ContentShell
      showBackButton
      onBackButtonClick={cancel}
      title={`${id === "create" ? "Create" : "Edit"} Quick Bet`}
      loading={loading}
      actions={
        <Box className={styles.actionBtnContainer}>
          <Button
            type="submit"
            variant="contained"
            style={{ backgroundColor: "red", color: "white" }}
            onClick={cancel}
          >
            Cancel
          </Button>
          <Button type="submit" variant="contained" onClick={submit}>
            Save
          </Button>
        </Box>
      }
    >
      <Box className={(styles as any).container}>
        <Box className={styles.firstFormContainer}>
          <Box className={styles.field}>
            <SimpleSelectInput
              name="currency"
              label="Currency"
              options={sessionHelper.getCurrencyOptions()}
              value={quickBetDetails.currency}
              onChange={onChange}
            />
          </Box>
          <Box className={styles.field}>
            <SimpleSelectInput
              label="Territory"
              name="territory"
              options={sessionHelper.countryOptions}
              value={quickBetDetails.territory}
              onChange={onChange}
            />
          </Box>
        </Box>

        <Typography style={{ fontWeight: "bold" }}>Bets</Typography>
        <Divider variant="fullWidth" />
        <Box style={{ display: "flex", marginTop: 10, gap: 5 }}>
          {Array.from({ length: 4 }, (v, k) => {
            return (
              <Box className={styles.field}>
                <SimpleTextInput
                  label={`Bets-${k}`}
                  name="bets"
                  value={quickBetDetails.bets[k]}
                  onChange={(e) => onChange(e, k)}
                />
              </Box>
            );
          })}
        </Box>
      </Box>
    </ContentShell>
  );
};

export default QuickBetEditor;
